import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';


const CharacterProfileForm = ({ character, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    class: '',
    level: '',
    isMain: false,
    guildNotes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (character) {
      setFormData({
        name: character?.name || '',
        class: character?.class || '',
        level: character?.level || '',
        isMain: character?.isMain || false,
        guildNotes: character?.guildNotes || ''
      });
    }
  }, [character]);

  const classOptions = [
    { value: 'Warrior', label: 'Warrior' },
    { value: 'Cleric', label: 'Cleric' },
    { value: 'Rogue', label: 'Rogue' },
    { value: 'Wizard', label: 'Wizard' },
    { value: 'Ranger', label: 'Ranger' },
    { value: 'Paladin', label: 'Paladin' },
    { value: 'Necromancer', label: 'Necromancer' },
    { value: 'Enchanter', label: 'Enchanter' },
    { value: 'Magician', label: 'Magician' },
    { value: 'Bard', label: 'Bard' },
    { value: 'Monk', label: 'Monk' },
    { value: 'Druid', label: 'Druid' },
    { value: 'Shaman', label: 'Shaman' },
    { value: 'Berserker', label: 'Berserker' },
    { value: 'Beastlord', label: 'Beastlord' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Character name is required';
    } else if (formData?.name?.length < 3) {
      newErrors.name = 'Character name must be at least 3 characters';
    }

    if (!formData?.class) {
      newErrors.class = 'Class selection is required';
    }

    if (!formData?.level) {
      newErrors.level = 'Level is required';
    } else if (formData?.level < 1 || formData?.level > 75) {
      newErrors.level = 'Level must be between 1 and 75';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSaving(true);
    
    setTimeout(() => {
      onSave({
        ...character,
        ...formData,
        level: parseInt(formData?.level)
      });
      setIsSaving(false);
    }, 800);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Character Name"
          type="text"
          placeholder="Enter character name"
          value={formData?.name}
          onChange={(e) => handleChange('name', e?.target?.value)}
          error={errors?.name}
          required
        />

        <Select
          label="Class"
          placeholder="Select class"
          options={classOptions}
          value={formData?.class}
          onChange={(value) => handleChange('class', value)}
          error={errors?.class}
          required
        />
      </div>
      <Input
        label="Level"
        type="number"
        placeholder="Enter level (1-75)"
        value={formData?.level}
        onChange={(e) => handleChange('level', e?.target?.value)}
        error={errors?.level}
        min={1}
        max={75}
        required
      />
      <Checkbox
        label="Set as Main Character"
        description="Your main character will be displayed by default"
        checked={formData?.isMain}
        onChange={(e) => handleChange('isMain', e?.target?.checked)}
      />
      <Input
        label="Guild Notes"
        type="text"
        placeholder="Add any notes about this character"
        value={formData?.guildNotes}
        onChange={(e) => handleChange('guildNotes', e?.target?.value)}
        description="Optional notes visible to guild officers"
      />
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button
          type="submit"
          variant="default"
          loading={isSaving}
          iconName="Save"
          iconPosition="left"
          fullWidth
        >
          {character?.id ? 'Update Character' : 'Create Character'}
        </Button>
        
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSaving}
          fullWidth
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default CharacterProfileForm;